const bodyPartText = (value) => String(value || "").trim();

export const medicationTopicalBodyPartOptions = [
  ["ALL_BODY", "All body"],
  ["HAIR", "Hair"],
  ["SCALP", "Scalp"],
  ["FACE", "Face"],
  ["NOSE", "Nose"],
  ["LEFT_CHEEK", "Left cheek"],
  ["RIGHT_CHEEK", "Right cheek"],
  ["LEFT_EAR", "Left ear"],
  ["RIGHT_EAR", "Right ear"],
  ["NECK", "Neck"],
  ["LEFT_ARMPIT", "Left armpit"],
  ["RIGHT_ARMPIT", "Right armpit"],
  ["LEFT_SHOULDER", "Left shoulder"],
  ["RIGHT_SHOULDER", "Right shoulder"],
  ["CHEST", "Chest"],
  ["ABDOMEN", "Abdomen"],
  ["BACK", "Back"],
  ["LEFT_ARM", "Left arm"],
  ["RIGHT_ARM", "Right arm"],
  ["LEFT_ELBOW", "Left elbow"],
  ["RIGHT_ELBOW", "Right elbow"],
  ["LEFT_HAND", "Left hand"],
  ["RIGHT_HAND", "Right hand"],
  ["LEFT_PALM", "Left palm"],
  ["RIGHT_PALM", "Right palm"],
  ["LEFT_FINGERS", "Left fingers"],
  ["RIGHT_FINGERS", "Right fingers"],
  ["GROIN", "Groin"],
  ["BUTTOCKS", "Buttocks"],
  ["LEFT_LEG", "Left leg"],
  ["RIGHT_LEG", "Right leg"],
  ["LEFT_THIGH", "Left thigh"],
  ["RIGHT_THIGH", "Right thigh"],
  ["LEFT_KNEE", "Left knee"],
  ["RIGHT_KNEE", "Right knee"],
  ["LEFT_SHIN", "Left shin"],
  ["RIGHT_SHIN", "Right shin"],
  ["LEFT_FOOT", "Left foot"],
  ["RIGHT_FOOT", "Right foot"],
  ["LEFT_TOES", "Left toes"],
  ["RIGHT_TOES", "Right toes"],
  ["WOUND_SITE", "Wound site"],
  ["AFFECTED_AREA", "Affected area"],
  ["OTHER", "Other"],
];

export const medicationInjectionBodyPartOptions = [
  ["LEFT_DELTOID", "Left deltoid / shoulder"],
  ["RIGHT_DELTOID", "Right deltoid / shoulder"],
  ["LEFT_UPPER_ARM", "Left upper arm"],
  ["RIGHT_UPPER_ARM", "Right upper arm"],
  ["LEFT_THIGH", "Left thigh"],
  ["RIGHT_THIGH", "Right thigh"],
  ["ABDOMEN", "Abdomen"],
  ["LEFT_BUTTOCK", "Left buttock"],
  ["RIGHT_BUTTOCK", "Right buttock"],
  ["LEFT_HIP", "Left hip"],
  ["RIGHT_HIP", "Right hip"],
  ["IV_SITE", "IV site"],
  ["OTHER", "Other"],
];

export const medicationInjectionRouteValues = new Set(["INJECTION", "SUBCUTANEOUS", "INTRAMUSCULAR", "INTRAVENOUS"]);
export const medicationTopicalRouteValues = new Set(["TOPICAL", "BODY_AREA"]);

const routeLabelMap = {
  ORAL: "ORAL",
  TOPICAL: "TOPICAL",
  "BODY AREA / AFFECTED AREA": "BODY_AREA",
  BODY_AREA_AFFECTED_AREA: "BODY_AREA",
  BODY_AREA: "BODY_AREA",
  INJECTION: "INJECTION",
  SUBCUTANEOUS: "SUBCUTANEOUS",
  INTRAMUSCULAR: "INTRAMUSCULAR",
  INTRAVENOUS: "INTRAVENOUS",
  INHALATION: "INHALATION",
  OPHTHALMIC: "OPHTHALMIC",
  OTIC: "OTIC",
  NASAL: "NASAL",
  RECTAL: "RECTAL",
};

export const normalizeMedicationRoute = (routeValue = "") =>
  routeLabelMap[bodyPartText(routeValue).toUpperCase().replace(/[^A-Z0-9]+/g, "_")]
  || routeLabelMap[bodyPartText(routeValue).toUpperCase()]
  || bodyPartText(routeValue).toUpperCase().replace(/[^A-Z0-9]+/g, "_");

export const medicationBodyPartOptionsForRoute = (routeValue = "") => {
  const route = normalizeMedicationRoute(routeValue);
  if (medicationInjectionRouteValues.has(route)) return medicationInjectionBodyPartOptions;
  if (medicationTopicalRouteValues.has(route)) return medicationTopicalBodyPartOptions;
  return [];
};

export const medicationRouteNeedsBodyPart = (routeValue = "") =>
  medicationBodyPartOptionsForRoute(routeValue).length > 0;

export const medicationBodyPartValues = (value) => {
  if (Array.isArray(value)) return value.map(bodyPartText).filter(Boolean);
  return String(value || "").split(/\s*(?:,|;|\|)\s*/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => (/^OTHER\s*:/i.test(entry) ? "OTHER" : entry));
};

export const medicationBodyPartOtherValue = (value) => {
  const source = Array.isArray(value) ? value.join(", ") : String(value || "");
  const match = source.match(/(?:^|[,;|]\s*)OTHER\s*:\s*([^,;|]+)/i);
  return match ? match[1].trim() : "";
};

export const medicationBodyPartPayload = (value, otherValue = "") =>
  medicationBodyPartValues(value)
    .map((entry) => (entry === "OTHER" && bodyPartText(otherValue) ? `OTHER: ${bodyPartText(otherValue)}` : entry))
    .join(", ");

export const medicationBodyPartNeedsOtherText = (value) =>
  medicationBodyPartValues(value).includes("OTHER");

export const medicationBodyPartLabel = (value, routeValue = "") => {
  const options = medicationBodyPartOptionsForRoute(routeValue);
  return (Array.isArray(value) ? value : String(value || "").split(/\s*(?:,|;|\|)\s*/))
    .map((entry) => bodyPartText(entry))
    .filter(Boolean)
    .map((bodyPart) => {
      if (/^OTHER\s*:/i.test(bodyPart)) return bodyPart.replace(/^OTHER\s*:/i, "Other:");
      return options.find(([option]) => option === bodyPart)?.[1] || bodyPart;
    })
    .join(", ");
};

const medicationDiagramParts = [
  ["HAIR", "HAIR", "hair", "M38 12h28l-4 16H42z", "front"],
  ["HEAD", "SCALP", "head front", "M52 15a13 13 0 1 0 0.1 0", "front"],
  ["FACE", "FACE", "face", "M42 30h20v13H42z", "front"],
  ["NOSE", "NOSE", "nose", "M49 32h6v8h-6z", "front"],
  ["LEFT_CHEEK", "LEFT_CHEEK", "left cheek", "M42 35h7v7h-7z", "front"],
  ["RIGHT_CHEEK", "RIGHT_CHEEK", "right cheek", "M55 35h7v7h-7z", "front"],
  ["LEFT_EAR", "LEFT_EAR", "left ear", "M35 27h6v11h-6z", "front"],
  ["RIGHT_EAR", "RIGHT_EAR", "right ear", "M63 27h6v11h-6z", "front"],
  ["NECK", "NECK", "neck", "M46 45h12v10H46z", "front"],
  ["LEFT_SHOULDER", "LEFT_SHOULDER", "left shoulder", "M28 54h14v14H25z", "front"],
  ["RIGHT_SHOULDER", "RIGHT_SHOULDER", "right shoulder", "M62 54h14l3 14H62z", "front"],
  ["LEFT_DELTOID", "LEFT_DELTOID", "left deltoid", "M25 64h10l-2 22H21z", "front"],
  ["RIGHT_DELTOID", "RIGHT_DELTOID", "right deltoid", "M69 64h10l4 22H71z", "front"],
  ["CHEST", "CHEST", "chest", "M32 58h40l-5 38H37z", "front"],
  ["LEFT_ARMPIT", "LEFT_ARMPIT", "left armpit", "M29 70h9v12h-9z", "front"],
  ["RIGHT_ARMPIT", "RIGHT_ARMPIT", "right armpit", "M66 70h9v12h-9z", "front"],
  ["ABDOMEN", "ABDOMEN", "abdomen", "M37 98h30v42H37z", "front"],
  ["LEFT_ARM", "LEFT_ARM", "left arm", "M22 61h11l-6 70H14z", "front"],
  ["RIGHT_ARM", "RIGHT_ARM", "right arm", "M72 61h11l8 70H78z", "front"],
  ["LEFT_ELBOW", "LEFT_ELBOW", "left elbow", "M16 96h14v18H14z", "front"],
  ["RIGHT_ELBOW", "RIGHT_ELBOW", "right elbow", "M74 96h14l2 18H76z", "front"],
  ["LEFT_HAND", "LEFT_HAND", "left hand", "M12 132h15v18H10z", "front"],
  ["RIGHT_HAND", "RIGHT_HAND", "right hand", "M77 132h15l2 18H79z", "front"],
  ["LEFT_PALM", "LEFT_PALM", "left palm", "M14 136h10v9H14z", "front"],
  ["RIGHT_PALM", "RIGHT_PALM", "right palm", "M80 136h10v9H80z", "front"],
  ["LEFT_FINGERS", "LEFT_FINGERS", "left fingers", "M10 146h17v9H8z", "front"],
  ["RIGHT_FINGERS", "RIGHT_FINGERS", "right fingers", "M77 146h17l2 9H79z", "front"],
  ["GROIN", "GROIN", "groin", "M38 142h28l-14 18z", "front"],
  ["LEFT_LEG", "LEFT_LEG", "left leg", "M35 160h15l-4 72H30z", "front"],
  ["RIGHT_LEG", "RIGHT_LEG", "right leg", "M54 160h15l5 72H58z", "front"],
  ["LEFT_KNEE", "LEFT_KNEE", "left knee", "M31 194h17v19H30z", "front"],
  ["RIGHT_KNEE", "RIGHT_KNEE", "right knee", "M56 194h17l1 19H57z", "front"],
  ["LEFT_FOOT", "LEFT_FOOT", "left foot", "M29 234h18v12H23z", "front"],
  ["RIGHT_FOOT", "RIGHT_FOOT", "right foot", "M57 234h18l6 12H58z", "front"],
  ["LEFT_TOES", "LEFT_TOES", "left toes", "M22 244h25v7H20z", "front"],
  ["RIGHT_TOES", "RIGHT_TOES", "right toes", "M57 244h27l2 7H58z", "front"],
  ["LEFT_UPPER_ARM", "LEFT_UPPER_ARM", "left upper arm", "M22 61h11l-3 32H17z", "front"],
  ["RIGHT_UPPER_ARM", "RIGHT_UPPER_ARM", "right upper arm", "M72 61h11l5 32H75z", "front"],
  ["LEFT_THIGH", "LEFT_THIGH", "left thigh", "M35 160h15l-2 34H32z", "front"],
  ["RIGHT_THIGH", "RIGHT_THIGH", "right thigh", "M54 160h15l3 34H56z", "front"],
  ["LEFT_SHIN", "LEFT_SHIN", "left shin", "M31 214h16l-2 20H30z", "front"],
  ["RIGHT_SHIN", "RIGHT_SHIN", "right shin", "M58 214h16v20H59z", "front"],
  ["HAIR_BACK", "HAIR", "hair", "M38 12h28l-4 16H42z", "back"],
  ["LEFT_EAR_BACK", "LEFT_EAR", "left ear", "M35 27h6v11h-6z", "back"],
  ["RIGHT_EAR_BACK", "RIGHT_EAR", "right ear", "M63 27h6v11h-6z", "back"],
  ["LEFT_SHOULDER_BACK", "LEFT_SHOULDER", "left shoulder", "M28 54h14v14H25z", "back"],
  ["RIGHT_SHOULDER_BACK", "RIGHT_SHOULDER", "right shoulder", "M62 54h14l3 14H62z", "back"],
  ["LEFT_DELTOID_BACK", "LEFT_DELTOID", "left deltoid", "M25 64h10l-2 22H21z", "back"],
  ["RIGHT_DELTOID_BACK", "RIGHT_DELTOID", "right deltoid", "M69 64h10l4 22H71z", "back"],
  ["BACK", "BACK", "back", "M32 58h40l-5 83H37z", "back"],
  ["LEFT_ELBOW_BACK", "LEFT_ELBOW", "left elbow", "M16 96h14v18H14z", "back"],
  ["RIGHT_ELBOW_BACK", "RIGHT_ELBOW", "right elbow", "M74 96h14l2 18H76z", "back"],
  ["LEFT_ARMPIT_BACK", "LEFT_ARMPIT", "left armpit", "M29 70h9v12h-9z", "back"],
  ["RIGHT_ARMPIT_BACK", "RIGHT_ARMPIT", "right armpit", "M66 70h9v12h-9z", "back"],
  ["LEFT_BUTTOCK", "LEFT_BUTTOCK", "left buttock", "M34 142h18v27H31z", "back"],
  ["RIGHT_BUTTOCK", "RIGHT_BUTTOCK", "right buttock", "M53 142h18l3 27H53z", "back"],
  ["BUTTOCKS", "BUTTOCKS", "buttocks", "M31 142h43v28H31z", "back"],
  ["LEFT_HIP", "LEFT_HIP", "left hip", "M29 132h22v24H32z", "back"],
  ["RIGHT_HIP", "RIGHT_HIP", "right hip", "M53 132h22l-3 24H53z", "back"],
  ["LEFT_KNEE_BACK", "LEFT_KNEE", "left knee", "M31 194h17v19H30z", "back"],
  ["RIGHT_KNEE_BACK", "RIGHT_KNEE", "right knee", "M56 194h17l1 19H57z", "back"],
  ["LEFT_SHIN_BACK", "LEFT_SHIN", "left shin", "M31 214h16l-2 20H30z", "back"],
  ["RIGHT_SHIN_BACK", "RIGHT_SHIN", "right shin", "M58 214h16v20H59z", "back"],
];

export function MedicationBodyPartPicker({ onChange, onOtherChange, options = [], otherValue = "", route, value }) {
  const selectedValues = medicationBodyPartValues(value);
  const selectedSet = new Set(selectedValues);
  const optionValues = new Set(options.map(([optionValue]) => optionValue));
  const availableDiagramParts = medicationDiagramParts.filter((part) => optionValues.has(part[1]));
  const selectPart = (nextValue) => {
    if (!optionValues.has(nextValue)) return;
    if (nextValue === "ALL_BODY") {
      onChange(selectedSet.has("ALL_BODY") ? [] : ["ALL_BODY"]);
      return;
    }
    const withoutAllBody = selectedValues.filter((entry) => entry !== "ALL_BODY");
    onChange(selectedSet.has(nextValue)
      ? withoutAllBody.filter((entry) => entry !== nextValue)
      : [...withoutAllBody, nextValue]);
  };
  const handleKeyDown = (event, nextValue) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectPart(nextValue);
    }
  };

  return (
    <div className="medication-body-part-selector">
      <div className="medication-body-diagrams" aria-label="Body part diagram">
        {["front", "back"].map((view) => (
          <div className="medication-body-diagram-card" key={view}>
            <span>{view === "front" ? "Front" : "Back"}</span>
            <svg className="medication-body-diagram" role="img" viewBox="0 0 104 252" aria-label={`${view} body diagram`}>
              <title>{view === "front" ? "Front body" : "Back body"}</title>
              <path className={`medication-body-outline${selectedSet.has("ALL_BODY") ? " is-selected" : ""}`} d="M52 4a20 20 0 0 1 12 36v12h18c8 0 15 6 16 14l9 74c1 8-5 15-13 15h-8l2 83c0 8-6 14-14 14H60c-4 0-7-3-8-7-1 4-4 7-8 7H30c-8 0-14-6-14-14l2-83h-8c-8 0-14-7-13-15l9-74c1-8 8-14 16-14h18V40A20 20 0 0 1 52 4z" />
              {availableDiagramParts.filter(([, , , , partView]) => partView === view).map(([, targetValue, label, path]) => (
                <path
                  aria-label={label}
                  className={`medication-body-hotspot${selectedSet.has(targetValue) || selectedSet.has("ALL_BODY") ? " is-selected" : ""}`}
                  d={path}
                  key={`${view}-${targetValue}`}
                  onClick={() => selectPart(targetValue)}
                  onKeyDown={(event) => handleKeyDown(event, targetValue)}
                  role="button"
                  tabIndex={0}
                >
                  <title>{medicationBodyPartLabel(targetValue, route)}</title>
                </path>
              ))}
            </svg>
          </div>
        ))}
      </div>
      <div className="medication-body-part-picker">
        {options.map(([optionValue, label]) => (
          <button
            className={selectedSet.has(optionValue) ? "is-selected" : ""}
            key={optionValue}
            type="button"
            onClick={() => selectPart(optionValue)}
          >
            {label}
          </button>
        ))}
      </div>
      {selectedSet.has("OTHER") ? (
        <label className="ncp-form-field medication-body-part-other">
          <span>Specify other body part/site</span>
          <input
            value={otherValue}
            onChange={(event) => onOtherChange?.(event.target.value)}
            placeholder="Enter body part or administration site"
          />
        </label>
      ) : null}
    </div>
  );
}

export default MedicationBodyPartPicker;
